// IndexedDB-backed data layer for the core POS.
// This replaces the scattered mock data that each component used to keep
// privately, so products edited on the Products page show up in the POS,
// and completed sales are persisted across reloads.
//
// Storage is IndexedDB (see services/idb.ts) rather than localStorage, which
// lifts the old ~5 MB quota ceiling that the growing sales array pushed
// against. Every accessor is therefore async (Promise-returning); callers await
// them. The value format is unchanged — each key still holds a JSON string —
// so a device upgrading from the localStorage build keeps all its data via the
// one-time import in idb.initDb().

import {
  Product,
  Transaction,
  PosConfig,
  Customer,
  Supplier,
  Salesman,
  ServicePoint,
  Coupon,
  Recipe,
  CashFlowEntry,
  StockMovement,
  PurchaseOrder,
  ShiftRecord,
  PrintSettings,
  AppToggleSettings,
  TableOrder,
  TableLock,
  KotTicket,
  Category,
  UnitGroup,
  AttributeDef,
  AuditEvent,
  User,
  Role,
  ProductChange,
  ProductHistoryEntry,
  Room,
  RoomBooking,
  TransportInfo,
  OnlineOrder,
  ActiveShift,
  DiscountSlab,
} from '../types';
import { NAV_ITEMS } from '../navConfig';
import { idbGetRaw, idbSetRaw, cachedRawKey } from './idb';

const KEYS = {
  products: 'sconnect.products',
  sales: 'sconnect.sales',
  config: 'sconnect.config',
  customers: 'sconnect.customers',
  suppliers: 'sconnect.suppliers',
  salesmen: 'sconnect.salesmen',
  servicePoints: 'sconnect.servicePoints',
  coupons: 'sconnect.coupons',
  recipes: 'sconnect.recipes',
  categories: 'sconnect.categories',
  unitGroups: 'sconnect.unitGroups',
  attributes: 'sconnect.attributes',
  auditEvents: 'sconnect.auditEvents',
  users: 'sconnect.users',
  roles: 'sconnect.roles',
  productHistory: 'sconnect.productHistory',
  currentUser: 'sconnect.currentUser',
  rooms: 'sconnect.rooms',
  roomBookings: 'sconnect.roomBookings',
  cashflow: 'sconnect.cashflow',
  movements: 'sconnect.movements',
  orders: 'sconnect.orders',
  shifts: 'sconnect.shifts',
  activeShift: 'sconnect.activeShift',
  discountSlabs: 'sconnect.discountSlabs',
  printSettings: 'sconnect.printSettings',
  appSettings: 'sconnect.appSettings',
  billCounter: 'sconnect.billCounter',
  invoiceCounter: 'sconnect.invoiceCounter',
  openingBalance: 'sconnect.openingBalance',
  tables: 'sconnect.tables',
  tableOrders: 'sconnect.tableOrders',
  tableLocks: 'sconnect.tableLocks',
  kots: 'sconnect.kots',
  kotCounter: 'sconnect.kotCounter',
  vehicles: 'sconnect.vehicles',
  onlineOrders: 'sconnect.onlineOrders',
  onlineOrderCounter: 'sconnect.onlineOrderCounter',
} as const;

export function newId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

// Raised when a write fails because the device storage quota is full. Thrown by
// write() instead of letting the store fail silently, so a sale is never treated
// as saved when it wasn't. The app shell turns the companion
// 'sconnect:storage-full' event into a prompt to archive old bills. IndexedDB's
// quota is far larger than localStorage's, so this is now a rare last resort
// (disk actually full) rather than a routine ceiling.
export class StorageFullError extends Error {
  constructor() {
    super(
      'Device storage is full, so the change could not be saved. ' +
        'Free space in Settings → Archive & Clear Old Bills, then try again.'
    );
    this.name = 'StorageFullError';
  }
}

// Cross-browser detection of the quota-exceeded DOMException that the storage
// engine throws when storage is full (different browsers use different
// names/codes for it).
function isQuotaError(e: unknown): boolean {
  return (
    typeof DOMException !== 'undefined' &&
    e instanceof DOMException &&
    (e.name === 'QuotaExceededError' ||
      e.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      e.code === 22 ||
      e.code === 1014)
  );
}

async function read<T>(key: string, fallback: T): Promise<T> {
  try {
    const raw = await idbGetRaw(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

async function write<T>(key: string, value: T): Promise<void> {
  try {
    await idbSetRaw(key, JSON.stringify(value));
  } catch (e) {
    if (isQuotaError(e)) {
      // Notify the app shell so it can prompt the user to archive old bills,
      // then throw a typed error so the caller doesn't treat the save as done.
      window.dispatchEvent(new Event('sconnect:storage-full'));
      throw new StorageFullError();
    }
    throw e;
  }
}

// ---- Server-authoritative sequence numbers ------------------------------
// Bill / invoice / KOT / online-order numbers must be unique across every
// terminal, so when a sync transport is connected it registers a provider here
// that leases numbers from the LAN server (services/sync.ts). Offline — or in
// single-device mode — the provider is absent (or returns null), and allocation
// falls back to the device-local counter exactly as before.
export type SeqKind = 'bill' | 'invoice' | 'kot' | 'onlineOrder';

let seqProvider: ((kind: SeqKind) => Promise<number | null>) | null = null;

export function registerSeqProvider(fn: ((kind: SeqKind) => Promise<number | null>) | null): void {
  seqProvider = fn;
}

async function allocateSeq(kind: SeqKind, key: string, floor: number): Promise<number> {
  const localLast = Math.max(await read<number>(key, floor), floor);
  if (seqProvider) {
    const n = await seqProvider(kind);
    if (n != null) {
      // Never let the local counter regress below the server's value, so
      // previews and any later offline fallback continue past it.
      await write(key, Math.max(localLast, n));
      return n;
    }
  }
  const next = localLast + 1;
  await write(key, next);
  return next;
}

// Current last-issued counters, used to seed an empty LAN server so shared
// numbering continues from this device's values rather than restarting.
export async function getCounters(): Promise<Record<SeqKind, number>> {
  return {
    bill: Math.max(await read<number>(KEYS.billCounter, BILL_NO_FLOOR), BILL_NO_FLOOR),
    invoice: Math.max(await read<number>(KEYS.invoiceCounter, INVOICE_NO_FLOOR), INVOICE_NO_FLOOR),
    kot: Math.max(await read<number>(KEYS.kotCounter, 0), 0),
    onlineOrder: Math.max(await read<number>(KEYS.onlineOrderCounter, 0), 0),
  };
}

// ---- Config (currency + tax) -------------------------------------------

const DEFAULT_CONFIG: PosConfig = {
  storeName: 'EPOS',
  currencySymbol: '₹',
  taxRatePercent: 5,
};

export async function getConfig(): Promise<PosConfig> {
  return { ...DEFAULT_CONFIG, ...(await read<Partial<PosConfig>>(KEYS.config, {})) };
}

export async function saveConfig(config: PosConfig): Promise<void> {
  await write(KEYS.config, config);
}

// ---- Products -----------------------------------------------------------

// 50 demo products across typical Indian retail/restaurant categories.
// Prepared-to-order items are non-stockable ("Service" in the grid); packaged
// goods carry stock. searchCode gives each a numeric quick-search code.
const SEED_PRODUCTS: Product[] = [
  // Beverages
  { id: '1', name: 'Coffee', price: 20, barcode: '890001', searchCode: '101', category: 'Beverages', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '2', name: 'Tea', price: 15, barcode: '890002', searchCode: '102', category: 'Beverages', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '3', name: 'Masala Chai', price: 25, barcode: '890003', searchCode: '103', category: 'Beverages', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '4', name: 'Water Bottle 1L', price: 20, barcode: '890004', searchCode: '104', category: 'Beverages', stock: 120, taxRatePercent: 0 },
  { id: '5', name: 'Cold Coffee', price: 60, barcode: '890005', searchCode: '105', category: 'Beverages', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '6', name: 'Fresh Lime Soda', price: 40, barcode: '890006', searchCode: '106', category: 'Beverages', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '7', name: 'Mango Lassi', price: 50, barcode: '890007', searchCode: '107', category: 'Beverages', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '8', name: 'Buttermilk', price: 25, barcode: '890008', searchCode: '108', category: 'Beverages', stock: 40, taxRatePercent: 0 },
  // Snacks
  { id: '9', name: 'Samosa', price: 15, barcode: '890009', searchCode: '109', category: 'Snacks', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '10', name: 'Kachori', price: 15, barcode: '890010', searchCode: '110', category: 'Snacks', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '11', name: 'Veg Puff', price: 25, barcode: '890011', searchCode: '111', category: 'Snacks', stock: 30, taxRatePercent: 5 },
  { id: '12', name: 'Potato Chips', price: 20, barcode: '890012', searchCode: '112', category: 'Snacks', stock: 80, taxRatePercent: 12 },
  { id: '13', name: 'Salted Peanuts', price: 30, barcode: '890013', searchCode: '113', category: 'Snacks', stock: 60, taxRatePercent: 12 },
  { id: '14', name: 'Popcorn', price: 30, barcode: '890014', searchCode: '114', category: 'Snacks', stock: 50, taxRatePercent: 12 },
  { id: '15', name: 'French Fries', price: 80, barcode: '890015', searchCode: '115', category: 'Snacks', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '16', name: 'Spring Roll', price: 70, barcode: '890016', searchCode: '116', category: 'Snacks', stock: 0, stockable: false, taxRatePercent: 5 },
  // Food
  { id: '17', name: 'Veg Sandwich', price: 60, barcode: '890017', searchCode: '117', category: 'Food', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '18', name: 'Grilled Sandwich', price: 80, barcode: '890018', searchCode: '118', category: 'Food', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '19', name: 'Veg Burger', price: 90, barcode: '890019', searchCode: '119', category: 'Food', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '20', name: 'Cheese Pizza', price: 150, barcode: '890020', searchCode: '120', category: 'Food', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '21', name: 'Masala Dosa', price: 70, barcode: '890021', searchCode: '121', category: 'Food', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '22', name: 'Idli Vada', price: 50, barcode: '890022', searchCode: '122', category: 'Food', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '23', name: 'Veg Fried Rice', price: 110, barcode: '890023', searchCode: '123', category: 'Food', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '24', name: 'Paneer Butter Masala', price: 180, barcode: '890024', searchCode: '124', category: 'Food', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '25', name: 'Butter Naan', price: 35, barcode: '890025', searchCode: '125', category: 'Food', stock: 0, stockable: false, taxRatePercent: 5 },
  { id: '26', name: 'Veg Thali', price: 120, barcode: '890026', searchCode: '126', category: 'Food', stock: 0, stockable: false, taxRatePercent: 5 },
  // Bakery
  { id: '27', name: 'White Bread', price: 40, barcode: '890027', searchCode: '127', category: 'Bakery', stock: 35, taxRatePercent: 0 },
  { id: '28', name: 'Brown Bread', price: 50, barcode: '890028', searchCode: '128', category: 'Bakery', stock: 25, taxRatePercent: 0 },
  { id: '29', name: 'Croissant', price: 60, barcode: '890029', searchCode: '129', category: 'Bakery', stock: 20, taxRatePercent: 5 },
  { id: '30', name: 'Chocolate Muffin', price: 45, barcode: '890030', searchCode: '130', category: 'Bakery', stock: 24, taxRatePercent: 5 },
  { id: '31', name: 'Black Forest Pastry', price: 75, barcode: '890031', searchCode: '131', category: 'Bakery', stock: 16, taxRatePercent: 5 },
  { id: '32', name: 'Cookies Pack', price: 55, barcode: '890032', searchCode: '132', category: 'Bakery', stock: 40, taxRatePercent: 12 },
  // Dairy
  { id: '33', name: 'Milk 500ml', price: 30, barcode: '890033', searchCode: '133', category: 'Dairy', stock: 60, taxRatePercent: 0 },
  { id: '34', name: 'Curd 400g', price: 35, barcode: '890034', searchCode: '134', category: 'Dairy', stock: 40, taxRatePercent: 0 },
  { id: '35', name: 'Paneer 200g', price: 90, barcode: '890035', searchCode: '135', category: 'Dairy', stock: 25, taxRatePercent: 5 },
  { id: '36', name: 'Butter 100g', price: 60, barcode: '890036', searchCode: '136', category: 'Dairy', stock: 30, taxRatePercent: 12 },
  { id: '37', name: 'Cheese Slices', price: 130, barcode: '890037', searchCode: '137', category: 'Dairy', stock: 20, taxRatePercent: 12 },
  { id: '38', name: 'Ice Cream Cup', price: 40, barcode: '890038', searchCode: '138', category: 'Dairy', stock: 45, taxRatePercent: 18 },
  // Grocery
  { id: '39', name: 'Basmati Rice 1kg', price: 95, barcode: '890039', searchCode: '139', category: 'Grocery', stock: 50, taxRatePercent: 5, weighable: true, unit: 'kg' },
  { id: '40', name: 'Toor Dal 1kg', price: 140, barcode: '890040', searchCode: '140', category: 'Grocery', stock: 40, taxRatePercent: 5, weighable: true, unit: 'kg' },
  { id: '41', name: 'Sunflower Oil 1L', price: 145, barcode: '890041', searchCode: '141', category: 'Grocery', stock: 35, taxRatePercent: 5 },
  { id: '42', name: 'Sugar 1kg', price: 45, barcode: '890042', searchCode: '142', category: 'Grocery', stock: 60, taxRatePercent: 5, weighable: true, unit: 'kg' },
  { id: '43', name: 'Wheat Flour 5kg', price: 240, barcode: '890043', searchCode: '143', category: 'Grocery', stock: 30, taxRatePercent: 5 },
  { id: '44', name: 'Tea Powder 250g', price: 120, barcode: '890044', searchCode: '144', category: 'Grocery', stock: 45, taxRatePercent: 5 },
  // Personal Care
  { id: '45', name: 'Toothpaste 100g', price: 55, barcode: '890045', searchCode: '145', category: 'Personal Care', stock: 50, taxRatePercent: 18 },
  { id: '46', name: 'Bath Soap', price: 35, barcode: '890046', searchCode: '146', category: 'Personal Care', stock: 70, taxRatePercent: 18 },
  { id: '47', name: 'Shampoo 200ml', price: 110, barcode: '890047', searchCode: '147', category: 'Personal Care', stock: 30, taxRatePercent: 18 },
  { id: '48', name: 'Hand Wash 250ml', price: 99, barcode: '890048', searchCode: '148', category: 'Personal Care', stock: 25, taxRatePercent: 18 },
  // Household
  { id: '49', name: 'Dishwash Liquid 500ml', price: 99, barcode: '890049', searchCode: '149', category: 'Household', stock: 30, taxRatePercent: 18 },
  { id: '50', name: 'Detergent 1kg', price: 120, barcode: '890050', searchCode: '150', category: 'Household', stock: 35, taxRatePercent: 18 },
];

export async function getProducts(): Promise<Product[]> {
  return read<Product[]>(KEYS.products, []);
}

export async function saveProducts(products: Product[]): Promise<void> {
  await write(KEYS.products, products);
}

export async function findByBarcode(barcode: string): Promise<Product | undefined> {
  return (await getProducts()).find((p) => p.barcode === barcode);
}

// ---- Sales --------------------------------------------------------------

export async function getSales(): Promise<Transaction[]> {
  return read<Transaction[]>(KEYS.sales, []);
}

export async function saveSale(sale: Transaction): Promise<void> {
  const sales = await getSales();
  sales.push(sale);
  await write(KEYS.sales, sales);
}

export async function saveSales(sales: Transaction[]): Promise<void> {
  await write(KEYS.sales, sales);
}

// ---- Sequential bill numbers -------------------------------------------
// A monotonic counter holding the last-issued bill number. `peek` shows the
// next number for previews without consuming it; `next` increments and returns
// it (called once, when a sale is recorded) so numbering is gap-free.
// The counter is floored at BILL_NO_FLOOR so the first bill is 1001 — this also
// lifts any lower value left over from testing, while never regressing a higher
// one.
const BILL_NO_FLOOR = 1000;

async function lastBillNo(): Promise<number> {
  return Math.max(await read<number>(KEYS.billCounter, BILL_NO_FLOOR), BILL_NO_FLOOR);
}

export async function peekNextBillNo(): Promise<string> {
  return String((await lastBillNo()) + 1);
}

export async function nextBillNo(): Promise<string> {
  return String(await allocateSeq('bill', KEYS.billCounter, BILL_NO_FLOOR));
}

// GST invoice number — a sequence distinct from the bill number (Android
// Bill.invoiceNumber). Only consumed for GST bills (not estimate / non-GST).
const INVOICE_NO_FLOOR = 0;
export async function nextInvoiceNo(): Promise<string> {
  return `INV-${await allocateSeq('invoice', KEYS.invoiceCounter, INVOICE_NO_FLOOR)}`;
}

// Adjust stock by a signed delta per product (negative reduces). Stock floors
// at 0 unless the "Stock Getting in Minus" app setting allows tracking
// negative stock (Android isStockGettingMinus).
export async function adjustStock(
  deltas: { productId: string; variantId?: string; delta: number }[]
): Promise<void> {
  const allowNegative = (await getAppSettings())['stock_getting_minus'] === true;
  const clamp = (n: number) => (allowNegative ? n : Math.max(0, n));
  const products = await getProducts();
  for (const { productId, variantId, delta } of deltas) {
    const product = products.find((p) => p.id === productId);
    if (!product) continue;
    const variants = product.variants ?? [];
    if (variants.length) {
      // A variant product's stock lives on the variants. An untargeted delta
      // (a sale from an older bill, a product-level stock-out) lands on the
      // default variant — or the first one — so the parent total never drifts from
      // the sum of its parts.
      const target =
        variants.find((v) => v.id === variantId) ??
        variants.find((v) => v.isDefault) ??
        variants[0];
      const before = target.stock ?? 0;
      target.stock = clamp(before + delta);
      // Apply to the parent exactly what the variant actually absorbed, so the
      // sum invariant holds even when the clamp bites.
      product.stock = clamp((product.stock ?? 0) + (target.stock - before));
      continue;
    }
    if (typeof product.stock === 'number') product.stock = clamp(product.stock + delta);
  }
  await saveProducts(products);
}

// The stock figure to show for a product: the sum of its variants when it has
// them (authoritative), else its own count.
export function stockOf(product: Product): number {
  if (product.variants?.length) {
    return product.variants.reduce((s, v) => s + (v.stock ?? 0), 0);
  }
  return product.stock ?? 0;
}

// Decrement stock for the products in a completed sale.
export async function decrementStock(
  items: { productId: string; variantId?: string; quantity: number }[]
): Promise<void> {
  await adjustStock(
    items.map((i) => ({ productId: i.productId, variantId: i.variantId, delta: -i.quantity }))
  );
}

// Bump each sold product's sale-frequency counter (Android LDProduct.keyCount),
// which orders the MENU CARD / hot-keys grid by best sellers.
export async function bumpKeyCounts(items: { productId: string }[]): Promise<void> {
  const products = await getProducts();
  for (const { productId } of items) {
    const product = products.find((p) => p.id === productId);
    if (product) product.keyCount = (product.keyCount ?? 0) + 1;
  }
  await saveProducts(products);
}

// ---- Customers ----------------------------------------------------------

export async function getCustomers(): Promise<Customer[]> {
  return read<Customer[]>(KEYS.customers, []);
}

export async function saveCustomers(customers: Customer[]): Promise<void> {
  await write(KEYS.customers, customers);
}

// ---- Suppliers ----------------------------------------------------------

export async function getSuppliers(): Promise<Supplier[]> {
  return read<Supplier[]>(KEYS.suppliers, []);
}

export async function saveSuppliers(suppliers: Supplier[]): Promise<void> {
  await write(KEYS.suppliers, suppliers);
}

// ---- Salesmen / employees ----------------------------------------------

export async function getSalesmen(): Promise<Salesman[]> {
  return read<Salesman[]>(KEYS.salesmen, []);
}

export async function saveSalesmen(salesmen: Salesman[]): Promise<void> {
  await write(KEYS.salesmen, salesmen);
}

// ---- Transport vehicles -------------------------------------------------
// Last-known transport detail per vehicle, used for the vehicle-no autocomplete
// on the Transport Detail form (Android keeps these in a TRANSPORT collection).

export async function getVehicles(): Promise<TransportInfo[]> {
  return read<TransportInfo[]>(KEYS.vehicles, []);
}

export async function saveVehicles(vehicles: TransportInfo[]): Promise<void> {
  await write(KEYS.vehicles, vehicles);
}

// ---- Service points -----------------------------------------------------

export async function getServicePoints(): Promise<ServicePoint[]> {
  return read<ServicePoint[]>(KEYS.servicePoints, []);
}

export async function saveServicePoints(points: ServicePoint[]): Promise<void> {
  await write(KEYS.servicePoints, points);
}

// ---- Coupons ------------------------------------------------------------

export async function getCoupons(): Promise<Coupon[]> {
  return read<Coupon[]>(KEYS.coupons, []);
}

export async function saveCoupons(coupons: Coupon[]): Promise<void> {
  await write(KEYS.coupons, coupons);
}

// ---- Opening balance (per-day shift opening cash) ----------------------

// yyyy-mm-dd → opening cash amount.
async function getOpeningBalances(): Promise<Record<string, number>> {
  return read<Record<string, number>>(KEYS.openingBalance, {});
}

export async function getOpeningBalance(dateStr: string): Promise<number> {
  return (await getOpeningBalances())[dateStr] ?? 0;
}

export async function setOpeningBalance(dateStr: string, amount: number): Promise<void> {
  await write(KEYS.openingBalance, { ...(await getOpeningBalances()), [dateStr]: amount });
}

// ---- Recipes / bill-of-materials ---------------------------------------

export async function getRecipes(): Promise<Recipe[]> {
  return read<Recipe[]>(KEYS.recipes, []);
}

export async function saveRecipes(recipes: Recipe[]): Promise<void> {
  await write(KEYS.recipes, recipes);
}

// ---- Categories ---------------------------------------------------------

export async function getCategories(): Promise<Category[]> {
  return read<Category[]>(KEYS.categories, []);
}

export async function saveCategories(categories: Category[]): Promise<void> {
  await write(KEYS.categories, categories);
}

// ---- Unit groups (multi-unit / UOM) -------------------------------------

export async function getUnitGroups(): Promise<UnitGroup[]> {
  return read<UnitGroup[]>(KEYS.unitGroups, []);
}

export async function saveUnitGroups(groups: UnitGroup[]): Promise<void> {
  await write(KEYS.unitGroups, groups);
}

// ---- Attribute master (product attributes) ------------------------------

export async function getAttributes(): Promise<AttributeDef[]> {
  return read<AttributeDef[]>(KEYS.attributes, []);
}

export async function saveAttributes(attributes: AttributeDef[]): Promise<void> {
  await write(KEYS.attributes, attributes);
}

// ---- Audit events -------------------------------------------------------

export async function getAuditEvents(): Promise<AuditEvent[]> {
  return read<AuditEvent[]>(KEYS.auditEvents, []);
}

export async function saveAuditEvents(events: AuditEvent[]): Promise<void> {
  await write(KEYS.auditEvents, events);
}

// Append one event (capped to the most recent 1000 to bound storage).
export async function logEvent(e: Omit<AuditEvent, 'id' | 'timestamp'>): Promise<void> {
  const events = await getAuditEvents();
  events.push({ ...e, id: newId(), timestamp: new Date().toISOString() });
  await saveAuditEvents(events.slice(-1000));
}

// ---- Users --------------------------------------------------------------

// Fresh installs ship one default operator — Admin / PIN 1234 — so the login
// screen works out of the box. Change the PIN (or add real operators) from
// the Users page; deleting every user disables the login gate entirely.
export const DEFAULT_ADMIN_PIN = '1234';
const DEFAULT_USERS: User[] = [
  { id: 'admin', name: 'Admin', pin: DEFAULT_ADMIN_PIN, role: 'admin', active: true },
];

export async function getUsers(): Promise<User[]> {
  return read<User[]>(KEYS.users, DEFAULT_USERS);
}

export async function saveUsers(users: User[]): Promise<void> {
  await write(KEYS.users, users);
}

export async function getCurrentUserId(): Promise<string | null> {
  return read<string | null>(KEYS.currentUser, null);
}

export async function setCurrentUserId(id: string | null): Promise<void> {
  await write(KEYS.currentUser, id);
  // Let the app shell (login gate, drawer) react to login/logout immediately.
  window.dispatchEvent(new Event('sconnect:auth'));
}

export async function getCurrentUser(): Promise<User | null> {
  const id = await getCurrentUserId();
  return id ? (await getUsers()).find((u) => u.id === id) ?? null : null;
}

// ---- Product history ------------------------------------------------------

// Fields shown in the Product Master change trail, with display labels.
const HISTORY_FIELDS: { key: keyof Product; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'regionalName', label: 'Regional Name' },
  { key: 'barcode', label: 'Barcode' },
  { key: 'category', label: 'Category' },
  { key: 'price', label: 'Selling Price' },
  { key: 'mrpPrice', label: 'MRP' },
  { key: 'stock', label: 'Stock' },
  { key: 'taxRatePercent', label: 'GST %' },
  { key: 'cessPercent', label: 'CESS %' },
  { key: 'unit', label: 'Unit' },
  { key: 'servicePoint', label: 'Service Point' },
  { key: 'stockable', label: 'Stockable' },
  { key: 'weighable', label: 'Weighable' },
  { key: 'discountPercent', label: 'Discount %' },
  { key: 'hsnCode', label: 'HSN Code' },
  { key: 'reorderLevel', label: 'Reorder Level' },
];

const histVal = (v: unknown): string => {
  if (v === undefined || v === null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'YES' : 'NO';
  if (Array.isArray(v)) return v.join(' / ');
  return String(v);
};

// Field-level diff between two versions of a product (price tiers included).
export function diffProducts(before: Product, after: Product): ProductChange[] {
  const changes: ProductChange[] = [];
  HISTORY_FIELDS.forEach(({ key, label }) => {
    const from = histVal(before[key]);
    const to = histVal(after[key]);
    if (from !== to) changes.push({ field: label, from, to });
  });
  const tiersBefore = histVal(before.priceTiers);
  const tiersAfter = histVal(after.priceTiers);
  if (tiersBefore !== tiersAfter) changes.push({ field: 'Price 1–5', from: tiersBefore, to: tiersAfter });
  return changes;
}

export async function getProductHistory(): Promise<ProductHistoryEntry[]> {
  return read<ProductHistoryEntry[]>(KEYS.productHistory, []);
}

// Append one entry stamped with the operator and time (capped to the most
// recent 2000 to bound storage).
export async function logProductHistory(
  e: Omit<ProductHistoryEntry, 'id' | 'timestamp' | 'userName'>
): Promise<void> {
  const entries = await getProductHistory();
  entries.push({
    ...e,
    id: newId(),
    timestamp: new Date().toISOString(),
    userName: (await getCurrentUser())?.name,
  });
  await write(KEYS.productHistory, entries.slice(-2000));
}

// ---- Roles & rights -------------------------------------------------------

const ALL_RIGHTS = NAV_ITEMS.map((i) => i.path);

// Seeded roles match the legacy hardcoded User.role union, so users saved
// before roles existed keep working unchanged.
const DEFAULT_ROLES: Role[] = [
  { id: 'admin', name: 'admin', rights: ALL_RIGHTS, system: true },
  {
    id: 'cashier',
    name: 'cashier',
    rights: [
      '/dashboard',
      '/', '/edit-bill', '/sale-return',
      '/cashflow', '/customers', '/customer-report', '/coupons', '/discounts',
      '/tables', '/captain', '/kitchen', '/kot-report',
      '/online-orders', '/swiggy-zomato',
      '/reports',
      '/opening-balance', '/shift-end', '/shifts',
      '/qr-login',
    ],
  },
  {
    id: 'waiter',
    name: 'user',
    rights: ['/dashboard', '/', '/tables', '/captain', '/kitchen', '/kot-report', '/qr-login'],
  },
];

export async function getRoles(): Promise<Role[]> {
  const roles = await read<Role[]>(KEYS.roles, DEFAULT_ROLES);
  // The admin role always carries every right, including paths added to the
  // drawer after the roles were last saved.
  return roles.map((r) => {
    // The built-in waiter role is now presented as "user"; roles saved before
    // the rename still carry the old name, so fix it up on read.
    const named = r.id === 'waiter' && r.name === 'waiter' ? { ...r, name: 'user' } : r;
    return named.system ? { ...named, rights: ALL_RIGHTS } : named;
  });
}

export async function saveRoles(roles: Role[]): Promise<void> {
  await write(KEYS.roles, roles);
}

// ---- Device terminal mode -------------------------------------------------
// What kind of station this physical device is (a role id), capped
// independently of whoever logs in. This is why a brand-new device can show a
// limited menu even though the only local account is the seeded Admin: users
// and roles are per-device and not synced, so without this a fresh install
// always logs in as Admin with every menu. Stored device-local (never synced
// through the op stream) and empty until the operator picks a mode on first
// launch. Changing it to an admin (system) role requires the admin PIN — see
// components/DeviceMode.tsx.
const deviceRoleCache = cachedRawKey('epos.deviceRole', '');

// The role id this device is capped to ('' = not yet chosen / first launch).
export function getDeviceRole(): string {
  return deviceRoleCache.get();
}

// Whether the operator has picked this device's terminal mode yet.
export function isDeviceRoleChosen(): boolean {
  return deviceRoleCache.get() !== '';
}

export async function setDeviceRole(roleId: string): Promise<void> {
  await deviceRoleCache.set(roleId);
  // Refresh the drawer + route guard right away (same event login uses).
  window.dispatchEvent(new Event('sconnect:auth'));
}

// Rights gate used by the drawer and the route guard. No operator logged in
// (or an unknown role id) restricts nothing, so a fresh install can never
// lock itself out; the dashboard — and the device-mode screen (the escape
// hatch for re-provisioning a capped terminal) — are always reachable.
export async function canAccess(path: string): Promise<boolean> {
  if (path === '/dashboard' || path === '/device-mode') return true;
  const roles = await getRoles();
  // The device terminal mode caps every menu, on top of the operator's role.
  // An admin (system) device role carries ALL_RIGHTS, so it caps nothing.
  const deviceRoleId = getDeviceRole();
  if (deviceRoleId) {
    const deviceRole = roles.find((r) => r.id === deviceRoleId);
    if (deviceRole && !deviceRole.rights.includes(path)) return false;
  }
  const user = await getCurrentUser();
  if (!user) return true;
  const role = roles.find((r) => r.id === user.role);
  if (!role) return true;
  return role.rights.includes(path);
}

// ---- Rooms (hotel module) -----------------------------------------------

export async function getRooms(): Promise<Room[]> {
  return read<Room[]>(KEYS.rooms, []);
}

export async function saveRooms(rooms: Room[]): Promise<void> {
  await write(KEYS.rooms, rooms);
}

export async function getRoomBookings(): Promise<RoomBooking[]> {
  return read<RoomBooking[]>(KEYS.roomBookings, []);
}

export async function saveRoomBookings(bookings: RoomBooking[]): Promise<void> {
  await write(KEYS.roomBookings, bookings);
}

// ---- CashFlow -----------------------------------------------------------

export async function getCashFlow(): Promise<CashFlowEntry[]> {
  return read<CashFlowEntry[]>(KEYS.cashflow, []);
}

export async function saveCashFlowEntry(entry: CashFlowEntry): Promise<void> {
  const all = await getCashFlow();
  all.push(entry);
  await write(KEYS.cashflow, all);
}

// Replace the whole cash-flow list in one write (used for state write-through,
// which avoids the read-modify-write races that concurrent appends would hit).
export async function saveCashFlow(entries: CashFlowEntry[]): Promise<void> {
  await write(KEYS.cashflow, entries);
}

// ---- Stock movements ----------------------------------------------------

export async function getMovements(): Promise<StockMovement[]> {
  return read<StockMovement[]>(KEYS.movements, []);
}

export async function saveMovement(movement: StockMovement): Promise<void> {
  const all = await getMovements();
  all.push(movement);
  await write(KEYS.movements, all);
}

// Replace the whole movement log in one write (used for state write-through,
// which avoids the read-modify-write races that concurrent appends would hit).
export async function saveMovements(movements: StockMovement[]): Promise<void> {
  await write(KEYS.movements, movements);
}

// ---- Purchase orders ----------------------------------------------------

export async function getOrders(): Promise<PurchaseOrder[]> {
  return read<PurchaseOrder[]>(KEYS.orders, []);
}

export async function saveOrders(orders: PurchaseOrder[]): Promise<void> {
  await write(KEYS.orders, orders);
}

// ---- Shifts -------------------------------------------------------------

export async function getShifts(): Promise<ShiftRecord[]> {
  return read<ShiftRecord[]>(KEYS.shifts, []);
}

export async function saveShift(shift: ShiftRecord): Promise<void> {
  const all = await getShifts();
  all.push(shift);
  await write(KEYS.shifts, all);
}

// Replace the whole shift-records list in one write (used by the op layer so a
// closed shift replicates to other terminals as an upsert).
export async function saveShifts(shifts: ShiftRecord[]): Promise<void> {
  await write(KEYS.shifts, shifts);
}

// ---- Shift sessions (Android firebase bean Shift) ------------------------
// One shift is open at a time; its id is "<dayId>-<shiftNo>". Bills recorded
// while it is open are stamped with that id. Closing a shift snapshots it into
// the shifts list (ShiftRecord) and optionally opens the next number.

export async function getActiveShift(): Promise<ActiveShift | null> {
  return read<ActiveShift | null>(KEYS.activeShift, null);
}

export async function setActiveShift(shift: ActiveShift | null): Promise<void> {
  await write(KEYS.activeShift, shift);
}

// Business-day id honoring "10 — Day Setting / A - Day End Time": bills rung
// up before the configured end hour (e.g. 2 AM) still belong to the previous
// day, so the id is computed from now minus that many hours.
async function todayId(): Promise<string> {
  const choice = (await getAppSettings())['day_end_time'];
  const hour =
    typeof choice === 'string' ? (choice.startsWith('12') ? 0 : parseInt(choice, 10) || 0) : 0;
  return new Date(Date.now() - hour * 3600_000).toISOString().split('T')[0];
}

// Open the next shift session for today (shift numbers restart each day).
export async function openShiftSession(openingCash: number): Promise<ActiveShift> {
  const dayId = await todayId();
  const lastClosed = (await getShifts())
    .filter((s) => s.dayId === dayId)
    .reduce((m, s) => Math.max(m, s.shiftNo ?? 0), 0);
  const shiftNo = lastClosed + 1;
  const shift: ActiveShift = {
    id: `${dayId}-${shiftNo}`,
    shiftNo,
    dayId,
    openedAt: new Date().toISOString(),
    openingCash,
  };
  await setActiveShift(shift);
  return shift;
}

// The shift to stamp on a sale right now: the open session if it belongs to
// today, else a freshly auto-opened one (Android opens shift 1 with the day).
export async function ensureActiveShift(): Promise<ActiveShift> {
  const current = await getActiveShift();
  if (current && current.dayId === (await todayId())) return current;
  return openShiftSession(await getOpeningBalance(await todayId()));
}

// ---- Discount slabs (Android firebase bean Discount) ---------------------

export async function getDiscountSlabs(): Promise<DiscountSlab[]> {
  return read<DiscountSlab[]>(KEYS.discountSlabs, []);
}

export async function saveDiscountSlabs(slabs: DiscountSlab[]): Promise<void> {
  await write(KEYS.discountSlabs, slabs);
}

// Slab lookup mirroring DiscountRepository.getDiscountPercentage: the first
// slab whose (startAmount, endAmount) range strictly contains the bill total.
// Pure and synchronous — callers pass the slab list they loaded via
// getDiscountSlabs().
export function discountPercentageFor(total: number, slabs: DiscountSlab[]): number {
  if (total <= 0) return 0;
  for (const s of slabs) {
    if (total > s.startAmount && total < s.endAmount) return s.percentage;
  }
  return 0;
}

// ---- Print settings -----------------------------------------------------

const DEFAULT_PRINT: PrintSettings = {
  // Printer Selection
  printerType: 'USB',
  paperSize: '3 Inch',
  boldFont: false,
  // Ethernet printer IPs
  ethernetPort: '9100',
  billingIp: '',
  kotIp: '',
  departmentIps: {},
  // Print Settings
  netTotalFont21: false,
  netTotalFont41: false,
  decimalDigits: '2',
  splitKitchen: 'None',
  openCashDrawer: false,
  copies: 1,
  // Header
  title: '',
  subTitle: '',
  fssai: '',
  address: '',
  gstNo: '',
  contactNo: '',
  email: '',
  serialNo: '',
  taxInvoice: 'TAX INVOICE',
  parcelName: '',
  bankName: '',
  bankBranch: '',
  bankIfsc: '',
  bankAccount: '',
  // Footer
  doNotPrintFooters: false,
  footer1: 'Thank you, visit again!',
  footer2: '',
  footer3: '',
  footer4: '',
  footer5: '',
  extraCharge: '',
  // Logo
  printHeaderLogo: false,
  printFooterLogo: false,
  // Derived
  paperWidth: '80mm',
};

export async function getPrintSettings(): Promise<PrintSettings> {
  return { ...DEFAULT_PRINT, ...(await read<Partial<PrintSettings>>(KEYS.printSettings, {})) };
}

export async function savePrintSettings(s: PrintSettings): Promise<void> {
  printSettingsCache = s;
  await write(KEYS.printSettings, s);
}

// ---- Synchronous settings snapshot ---------------------------------------
// Print/receipt builders (services/escpos.ts, receipt.ts, netPrinter.ts, …) are
// pure formatting functions invoked synchronously deep inside call trees, so
// they can't await storage. They read from these in-memory snapshots of the two
// small, hot config blobs instead. The snapshots are primed once at boot
// (primeSettingsCache, called from index.tsx after initDb) and refreshed on
// every save, so they always reflect what this device last stored.
let appSettingsCache: AppToggleSettings = {};
let printSettingsCache: PrintSettings = DEFAULT_PRINT;

export async function primeSettingsCache(): Promise<void> {
  appSettingsCache = await getAppSettings();
  printSettingsCache = await getPrintSettings();
}

export function getPrintSettingsSync(): PrintSettings {
  return printSettingsCache;
}

export function getAppSettingsSync(): AppToggleSettings {
  return appSettingsCache;
}

// ---- Restaurant: tables, running table orders, KOT tickets --------------

// The configurable list of table names/numbers. Defaults to T1..T12 so a
// fresh install has a usable floor plan; editable on the Tables screen.
const DEFAULT_TABLES: string[] = Array.from({ length: 12 }, (_, i) => `T${i + 1}`);

export async function getTables(): Promise<string[]> {
  return read<string[]>(KEYS.tables, []);
}

export async function saveTables(tables: string[]): Promise<void> {
  await write(KEYS.tables, tables);
}

// ---- First-run state -----------------------------------------------------
// A fresh install starts completely empty: no demo products, no default floor
// plan. The demo catalog is opt-in only, via Settings -> Load Demo Data.
// This flag is kept (outside the sconnect.* namespace, so "Clear All Data"
// cannot touch it) purely to record that demo data was loaded on purpose.
const SEEDED_FLAG = 'epos.seededDefaults';

// Explicit, opt-in re-seed for anyone who wants the demo catalog back (e.g.
// after Clear All Data, or to try the app with sample products) — this always
// overwrites, on demand, from a Settings button; nothing seeds automatically.
export async function loadDemoData(): Promise<void> {
  await write(KEYS.products, SEED_PRODUCTS);
  await write(KEYS.tables, DEFAULT_TABLES);
  await idbSetRaw(SEEDED_FLAG, '1');
}

export async function getTableOrders(): Promise<TableOrder[]> {
  return read<TableOrder[]>(KEYS.tableOrders, []);
}

export async function saveTableOrders(orders: TableOrder[]): Promise<void> {
  await write(KEYS.tableOrders, orders);
}

// Soft table locks (one terminal editing a table at a time). Kept alongside the
// running orders; synced through the op layer so a lock shows up on every
// terminal in real time.
export async function getTableLocks(): Promise<TableLock[]> {
  return read<TableLock[]>(KEYS.tableLocks, []);
}

export async function saveTableLocks(locks: TableLock[]): Promise<void> {
  await write(KEYS.tableLocks, locks);
}

export async function getKots(): Promise<KotTicket[]> {
  return read<KotTicket[]>(KEYS.kots, []);
}

export async function saveKots(kots: KotTicket[]): Promise<void> {
  await write(KEYS.kots, kots);
}

// Monotonic KOT number, floored so the first ticket is 1.
async function lastKotNo(): Promise<number> {
  return Math.max(await read<number>(KEYS.kotCounter, 0), 0);
}

export async function peekNextKotNo(): Promise<number> {
  return (await lastKotNo()) + 1;
}

export async function nextKotNo(): Promise<number> {
  return allocateSeq('kot', KEYS.kotCounter, 0);
}

// ---- Online orders (online_order_layout.xml) ----------------------------

export async function getOnlineOrders(): Promise<OnlineOrder[]> {
  return read<OnlineOrder[]>(KEYS.onlineOrders, []);
}

export async function saveOnlineOrders(orders: OnlineOrder[]): Promise<void> {
  await write(KEYS.onlineOrders, orders);
}

// Monotonic local order number for manually-entered orders that don't carry an
// aggregator reference. Floored so the first suggestion is 1.
export async function nextOnlineOrderNo(): Promise<number> {
  return allocateSeq('onlineOrder', KEYS.onlineOrderCounter, 0);
}

// ---- App settings (feature toggles, app_setting_layout.xml) --------------

export async function getAppSettings(): Promise<AppToggleSettings> {
  return read<AppToggleSettings>(KEYS.appSettings, {});
}

export async function saveAppSettings(s: AppToggleSettings): Promise<void> {
  appSettingsCache = s;
  await write(KEYS.appSettings, s);
}
